export type TimeMeridiem = 'AM' | 'PM';

export interface TimePickerParts {
  hour: string;
  minute: string;
  meridiem: TimeMeridiem;
}

export function pad2(value: number): string {
  return `${value}`.padStart(2, '0');
}

export function buildHourOptions(): string[] {
  return Array.from({ length: 12 }, (_, i) => pad2(i + 1));
}

export function buildMinuteOptions(): string[] {
  return Array.from({ length: 60 }, (_, i) => pad2(i));
}

export function time24ToParts(time24: string): TimePickerParts {
  if (!time24 || !time24.includes(':')) {
    const now = new Date();
    return time24ToParts(`${pad2(now.getHours())}:${pad2(now.getMinutes())}`);
  }
  const [hh, mm] = time24.split(':');
  let hour = parseInt(hh, 10);
  const meridiem: TimeMeridiem = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return {
    hour: pad2(hour),
    minute: pad2(parseInt(mm, 10) || 0),
    meridiem,
  };
}

export function partsToTime24(parts: TimePickerParts): string {
  let hourNum = parseInt(parts.hour, 10) % 12;
  if (parts.meridiem === 'PM') {
    hourNum += 12;
  }
  return `${pad2(hourNum)}:${parts.minute.padStart(2, '0')}`;
}

export function combineDateAndTimePartsToIso(
  date: Date | null,
  parts: TimePickerParts
): string | undefined {
  return combineDateAndTimeToIso(date, partsToTime24(parts));
}

export function formatScheduleDateLabel(date: Date | null): string {
  if (!date) {
    return '';
  }
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${date.getDate()} ${months[date.getMonth()]}, ${date.getFullYear()}`;
}

export function combineDateAndTimeToIso(date: Date | null, time: string): string | undefined {
  if (!date || !time || !time.trim()) {
    return undefined;
  }
  const parts = time.trim().split(':');
  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  if (isNaN(hours) || isNaN(minutes)) {
    return undefined;
  }
  const combined = new Date(date);
  combined.setHours(hours, minutes, 0, 0);
  if (isNaN(combined.getTime())) {
    return undefined;
  }
  return combined.toISOString();
}

export function splitIsoToDateAndTime(iso: string): { date: Date | null; time: string } {
  if (!iso) {
    return { date: null, time: '' };
  }
  const parsed = new Date(iso);
  if (isNaN(parsed.getTime())) {
    return { date: null, time: '' };
  }
  return {
    date: parsed,
    time: `${pad2(parsed.getHours())}:${pad2(parsed.getMinutes())}`,
  };
}

export function defaultScheduleDateTime(minutesFromNow = 2): { date: Date; time: string; parts: TimePickerParts } {
  const date = new Date(Date.now() + minutesFromNow * 60 * 1000);
  const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  return {
    date,
    time,
    parts: time24ToParts(time),
  };
}
